import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import { unwrapAsyncResult } from '../lib/asyncOperation'
import { openCounselSurveyReportPdf } from '../lib/counselSurveyReport'
import { tryShareFeedToKakao } from '../lib/kakao'
import { buildSurveyAnswerInputs, normalizeSurveyAnswerMap } from '../components/CounselSurveyForm'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import { formatApiDateTimeDisplay, formatMenuInstant } from '../lib/date'
import MenuGuideButton from '../components/MenuGuideButton'
import ManagedCustomerAutocomplete from '../components/ManagedCustomerAutocomplete'
import { AiDisclosure, AiFeatureBadge, AiGeneratedNotice } from '../components/AiDisclosure'
import { PageFrame, PageHeader } from '../ui/PageLayout'
function unwrap(res) {
  return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
}

function getTabLabel(tab) {
  if (tab === 'history') return '이력'
  if (tab === 'stats') return '통계'
  return '목록'
}

function formatDateTime(value) {
  return formatApiDateTimeDisplay(undefined, value)
}

function formatDateTimeCompact(value) {
  return formatMenuInstant({ createdAt: value }, 'createdAt')
}

function csvEscape(value) {
  const text = value == null ? '' : String(value)
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`
  }
  return text
}

function downloadCsv(filename, rows) {
  const bom = '\uFEFF'
  const content = bom + rows.map((line) => line.map(csvEscape).join(',')).join('\n')
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function absolutePublicUrl(value) {
  const raw = String(value || '').trim()
  if (!raw || typeof window === 'undefined') return ''
  if (/^https?:\/\//i.test(raw)) return raw
  return raw.startsWith('/') ? `${window.location.origin}${raw}` : `${window.location.origin}/${raw}`
}

function buildPublicCounselSurveyUrl(tokenPayload) {
  if (!tokenPayload || typeof window === 'undefined') return ''
  const payload = typeof tokenPayload === 'object' ? tokenPayload : { publicPath: tokenPayload }
  const ogUrl = absolutePublicUrl(payload.ogShareUrl || payload.shareUrl || payload.ogSharePath || payload.sharePath)
  if (ogUrl) return ogUrl

  const publicPath = payload.publicPath || payload.publicUrl
  if (!publicPath) return ''
  const token = String(publicPath).split('/').filter(Boolean).pop()
  if (!token) return ''
  const url = new URL(window.location.origin)
  url.searchParams.set('publicSurveyToken', token)
  return url.toString()
}

function getStatusLabel(status) {
  const key = String(status || '').toUpperCase()
  if (key === 'DRAFT') return '임시저장'
  if (key === 'ACTIVE') return '사용중'
  if (key === 'IN_PROGRESS') return '작성중'
  if (key === 'SUBMITTED') return '제출완료'
  if (key === 'ANALYZED') return '분석완료'
  if (key === 'PUBLISHED') return '배포완료'
  if (key === 'READY') return '준비완료'
  if (key === 'NOT_STARTED') return '미작성'
  return status || '-'
}

function getSessionTimeMeta(session) {
  const status = String(session?.status || '').toUpperCase()
  if (status === 'SUBMITTED' || status === 'ANALYZED') {
    return { label: '제출 시각', value: session?.submittedAt }
  }
  return { label: '임시 저장 시각', value: session?.lastSavedAt }
}

function buildQuestionMetaMap(schema) {
  const map = {}
  const sections = Array.isArray(schema?.sections) ? schema.sections : []
  sections.forEach((section, sectionIndex) => {
    const sectionTitle = section?.title || section?.key || `섹션 ${sectionIndex + 1}`
    const questions = Array.isArray(section?.questions) ? section.questions : []
    questions.forEach((question, questionIndex) => {
      if (!question?.key) return
      const options = Array.isArray(question?.options) ? question.options : []
      const optionLabelByValue = Object.fromEntries(options.map((option) => {
        const value = typeof option === 'string' ? option : (option?.value || option?.label || '')
        const label = typeof option === 'string' ? option : (option?.label || option?.value || '')
        return [String(value), label]
      }).filter(([value]) => !!value))
      map[String(question.key)] = {
        label: question?.label || question?.key,
        sectionTitle,
        questionType: question?.type || 'text',
        orderLabel: `Q${questionIndex + 1}`,
        question,
        optionLabelByValue,
      }
    })
  })
  return map
}

function formatAnswerText(answer, meta = {}) {
  if (answer === null || answer === undefined) return '-'
  if (String(meta?.question?.key || '') === 'residentRegistrationNumber') {
    const digits = String(answer).replace(/[^0-9]/g, '')
    if (!digits) return '-'
    if (digits.length <= 6) return digits
    return `${digits.slice(0, 6)}-${digits.slice(6, 7)}${'*'.repeat(Math.max(0, digits.length - 7))}`
  }
  const optionLabelByValue = meta?.optionLabelByValue || {}
  if (Array.isArray(answer)) {
    const labels = answer.map(item => optionLabelByValue[String(item)] || String(item)).filter(Boolean)
    return labels.join(', ') || '-'
  }
  if (typeof answer === 'boolean') return answer ? '예' : '아니오'
  if (typeof answer === 'object') {
    try { return JSON.stringify(answer, null, 2) } catch { return String(answer) }
  }
  return optionLabelByValue[String(answer)] || String(answer)
}

function getEditorFieldType(question) {
  const type = String(question?.type || 'text').toLowerCase()
  if (type === 'single-select') return 'radio'
  if (type === 'long-text') return 'textarea'
  if (type === 'multi-select') return 'checkbox'
  return type
}

function getOptionLabel(option) {
  if (typeof option === 'string') return option
  return option?.label || option?.value || '-'
}

function getOptionValue(option) {
  if (typeof option === 'string') return option
  return option?.value || option?.label || ''
}

function normalizeEditorValue(fieldType, raw) {
  if (raw === null || raw === undefined) return fieldType === 'checkbox' ? [] : ''
  if (fieldType === 'checkbox') return Array.isArray(raw) ? raw : []
  if (typeof raw === 'object') {
    try { return JSON.stringify(raw) } catch { return String(raw) }
  }
  return String(raw)
}

function inferBirthDateFromResidentNumber(raw) {
  const digits = String(raw || '').replace(/[^0-9]/g, '')
  if (digits.length < 6) return null

  const yy = Number(digits.slice(0, 2))
  const mm = Number(digits.slice(2, 4))
  const dd = Number(digits.slice(4, 6))
  if (!Number.isInteger(yy) || !Number.isInteger(mm) || !Number.isInteger(dd)) return null
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null

  let yearPrefix = null
  if (digits.length >= 7) {
    const centuryCode = digits[6]
    if (['1', '2', '5', '6'].includes(centuryCode)) yearPrefix = 1900
    if (['3', '4', '7', '8'].includes(centuryCode)) yearPrefix = 2000
    if (['9', '0'].includes(centuryCode)) yearPrefix = 1800
  }
  if (yearPrefix == null) {
    const currentTwoDigits = new Date().getFullYear() % 100
    yearPrefix = yy <= currentTwoDigits ? 2000 : 1900
  }

  const yyyy = yearPrefix + yy
  const normalized = `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`
  const date = new Date(`${normalized}T00:00:00`)
  if (Number.isNaN(date.getTime())) return null
  return normalized
}

function SessionAnswerEditor({ schema, answerMap, onChange }) {
  const sections = Array.isArray(schema?.sections) ? schema.sections : []

  const updateValue = (key, value) => onChange(prev => {
    const next = { ...prev, [key]: value }
    if (key === 'residentRegistrationNumber') {
      const inferredBirthDate = inferBirthDateFromResidentNumber(value)
      if (inferredBirthDate) {
        next.birthDate = inferredBirthDate
      }
    }
    return next
  })
  const toggleCheckbox = (key, value) => onChange(prev => {
    const current = Array.isArray(prev[key]) ? prev[key] : []
    return {
      ...prev,
      [key]: current.includes(value) ? current.filter(item => item !== value) : [...current, value],
    }
  })

  return (
    <div className="space-y-4">
      {sections.map((section, sectionIndex) => (
        <div key={section?.key || sectionIndex} className="rounded-2xl border p-4">
          <div className="text-sm font-semibold text-slate-900">{section?.title || `섹션 ${sectionIndex + 1}`}</div>
          {section?.description && <div className="mt-1 text-xs text-slate-500">{section.description}</div>}
          <div className="mt-4 space-y-4">
            {(Array.isArray(section?.questions) ? section.questions : []).map((question, questionIndex) => {
              const fieldType = getEditorFieldType(question)
              const options = Array.isArray(question?.options) ? question.options : []
              const value = normalizeEditorValue(fieldType, answerMap?.[question?.key])
              return (
                <div key={question?.key || `${sectionIndex}-${questionIndex}`} className="rounded-xl bg-slate-50 p-4">
                  <div className="text-sm font-semibold text-slate-900">{question?.label || question?.key || `문항 ${questionIndex + 1}`}</div>
                  {fieldType === 'textarea' && (
                    <textarea
                      className="mt-3 min-h-[96px] w-full rounded-lg border px-3 py-2 text-sm"
                      value={value}
                      onChange={(e) => updateValue(question.key, e.target.value)}
                    />
                  )}
                  {(fieldType === 'text' || fieldType === 'tel' || fieldType === 'date' || fieldType === 'number') && (
                    <input
                      type={question?.key === 'residentRegistrationNumber' ? 'password' : fieldType}
                      className="mt-3 w-full rounded-lg border px-3 py-2 text-sm"
                      value={value}
                      autoComplete={question?.key === 'residentRegistrationNumber' ? 'off' : undefined}
                      onChange={(e) => updateValue(question.key, e.target.value)}
                    />
                  )}
                  {fieldType === 'select' && (
                    <select
                      className="mt-3 w-full rounded-lg border px-3 py-2 text-sm"
                      value={value}
                      onChange={(e) => updateValue(question.key, e.target.value)}
                    >
                      <option value="">선택해 주세요</option>
                      {options.map(option => (
                        <option key={getOptionValue(option)} value={getOptionValue(option)}>{getOptionLabel(option)}</option>
                      ))}
                    </select>
                  )}
                  {fieldType === 'radio' && (
                    <div className="mt-3 grid gap-2 md:grid-cols-2">
                      {options.map(option => {
                        const optionValue = getOptionValue(option)
                        return (
                          <label key={optionValue} className="flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm">
                            <input
                              type="radio"
                              name={question.key}
                              checked={value === optionValue}
                              onChange={() => updateValue(question.key, optionValue)}
                            />
                            <span>{getOptionLabel(option)}</span>
                          </label>
                        )
                      })}
                    </div>
                  )}
                  {fieldType === 'checkbox' && (
                    <div className="mt-3 grid gap-2 md:grid-cols-2">
                      {options.map(option => {
                        const optionValue = getOptionValue(option)
                        const checked = Array.isArray(value) && value.includes(optionValue)
                        return (
                          <label key={optionValue} className="flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleCheckbox(question.key, optionValue)}
                            />
                            <span>{getOptionLabel(option)}</span>
                          </label>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

function SessionDetailModal({
  session,
  report,
  loading,
  error,
  onClose,
  onRegenerate,
  regenerating,
  onOpenPdf,
  openingPdf,
  reportHtml,
  reportHtmlLoading,
  reportHtmlError,
  answerViewMode,
  onChangeAnswerViewMode,
  onSaveEdit,
  savingEdit,
  onDelete,
  deleting,
  scrollReportIntoView = false,
}) {
  const questionMetaMap = useMemo(() => buildQuestionMetaMap(session?.survey || {}), [session])
  const answers = Array.isArray(session?.answers) ? session.answers : []
  const sessionTimeMeta = getSessionTimeMeta(session)
  const [editing, setEditing] = useState(false)
  const [customerName, setCustomerName] = useState('')
  const [answerMap, setAnswerMap] = useState({})
  const reportSectionRef = useRef(null)

  useEffect(() => {
    if (!scrollReportIntoView) return
    if (loading || error || !session) return
    const timer = window.setTimeout(() => {
      reportSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 200)
    return () => window.clearTimeout(timer)
  }, [scrollReportIntoView, loading, error, session, report])

  useEffect(() => {
    setEditing(false)
    setCustomerName(session?.customerName || '')
    setAnswerMap(normalizeSurveyAnswerMap(session?.answers || []))
  }, [session])

  const handleSaveEdit = async () => {
    if (!session?.survey) return
    await onSaveEdit?.({
      customerName,
      answers: buildSurveyAnswerInputs(session.survey, answerMap),
    })
    setEditing(false)
  }

  const handleDelete = async () => {
    if (!session?.id) return
    if (!window.confirm('이 설문 이력을 삭제할까요? 삭제 후 복구할 수 없습니다.')) return
    await onDelete?.(session.id)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[88vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">설문 상세</h2>
            <div className="mt-1 text-sm text-slate-500">이력 정보와 응답, 분석 리포트를 확인합니다.</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {!editing && (
              <button type="button" onClick={() => setEditing(true)} className="rounded border px-3 py-2 text-sm hover:bg-slate-50">
                수정
              </button>
            )}
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="rounded border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-60"
            >
              삭제
            </button>
            <button type="button" onClick={onClose} className="rounded border px-3 py-2 text-sm hover:bg-slate-50">닫기</button>
          </div>
        </div>

        {loading && <div className="mt-6 text-sm text-slate-500">상세 정보를 불러오는 중입니다.</div>}
        {error && <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">세션 상세를 불러오지 못했습니다.</div>}

        {!loading && !error && session && (
          <div className="mt-5 space-y-5">
            <div className="rounded-xl bg-slate-50 p-4 text-sm text-slate-700">
              <div>고객명: {session.customerName || '-'}</div>
              <div>설문: {session.templateName || '-'}</div>
              <div>방식: {session.entryMode === 'REMOTE' ? '링크작성' : '직접작성'}</div>
              <div>상태: {getStatusLabel(session.status)}</div>
              <div>{sessionTimeMeta.label}: {formatDateTime(sessionTimeMeta.value)}</div>
            </div>

            {editing ? (
              <div className="space-y-4">
                <div className="rounded-2xl border p-4">
                  <div className="text-sm font-semibold">설문 수정</div>
                  <div className="mt-3">
                    <label className="block text-xs font-semibold text-slate-600">고객명</label>
                    <input
                      className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                    />
                  </div>
                </div>
                <SessionAnswerEditor schema={session.survey || { sections: [] }} answerMap={answerMap} onChange={setAnswerMap} />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(false)
                      setCustomerName(session?.customerName || '')
                      setAnswerMap(normalizeSurveyAnswerMap(session?.answers || []))
                    }}
                    className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveEdit}
                    disabled={savingEdit}
                    className="rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                  >
                    {savingEdit ? '저장 중...' : '변경 저장'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div data-ai-disclosure="SurveySessionAiDisclosure" className="mb-4">
                  <AiDisclosure compact />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <label htmlFor={`answer-view-mode-${session?.id || 'current'}`} className="sr-only">
                      응답 보기
                    </label>
                    <select
                      id={`answer-view-mode-${session?.id || 'current'}`}
                      value={answerViewMode}
                      onChange={(event) => onChangeAnswerViewMode(event.target.value)}
                      className="h-10 min-w-[150px] rounded border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 focus:border-brand-navy focus:outline-none focus:ring-1 focus:ring-brand-navy/20"
                    >
                      <option value="question">문항 같이 보기</option>
                      <option value="answer">답변만 보기</option>
                    </select>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={onOpenPdf}
                      disabled={openingPdf || !report}
                      className="h-10 rounded bg-brand-navy px-3 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                    >
                      {openingPdf ? 'PDF 여는 중...' : 'PDF 리포트'}
                    </button>
                    <button
                      type="button"
                      onClick={onRegenerate}
                      disabled={regenerating}
                      className="h-10 rounded border px-3 text-sm hover:bg-slate-50 disabled:opacity-60"
                    >
                      {regenerating ? '재생성 중...' : '리포트 재생성'}
                    </button>
                  </div>
                </div>

                <div>
                  <div className="text-sm font-semibold">응답 목록</div>
                  <div className="mt-2 space-y-2">
                    {answers.map((answer, index) => {
                      const meta = questionMetaMap[answer.questionKey] || {}
                      const formatted = formatAnswerText(answer.answer, meta)
                      const isStructured = typeof answer.answer === 'object' && answer.answer !== null
                      return (
                        <div key={`${answer.questionKey}-${index}`} className="rounded-xl border p-4">
                          {answerViewMode === 'question' && (
                            <div className="mb-3 flex items-start justify-between gap-3">
                              <div>
                                <div className="text-sm font-semibold text-slate-900">{meta.label || answer.questionKey}</div>
                                <div className="mt-1 text-xs text-slate-500">{meta.sectionTitle || answer.sectionKey || '기본 섹션'}</div>
                              </div>
                              {meta.orderLabel && <span className="rounded bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">{meta.orderLabel}</span>}
                            </div>
                          )}
                          <div className="rounded-lg bg-slate-50 p-3">
                            {isStructured ? (
                              <pre className="overflow-auto whitespace-pre-wrap text-xs text-slate-700">{formatted}</pre>
                            ) : (
                              <div className="text-sm text-slate-800">{formatted}</div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                    {answers.length === 0 && <div className="text-sm text-slate-400">저장된 응답이 없습니다.</div>}
                  </div>
                </div>
              </>
            )}

            <div ref={reportSectionRef}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-semibold">분석 리포트</div>
                {report && (
                  <button
                    type="button"
                    onClick={onOpenPdf}
                    disabled={openingPdf}
                    className="rounded bg-brand-navy px-3 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                  >
                    {openingPdf ? 'PDF 여는 중...' : 'PDF 리포트'}
                  </button>
                )}
              </div>
              {!report && <div className="mt-2 text-sm text-slate-400">아직 생성된 리포트가 없습니다.</div>}
              {report && (
                <div className="mt-3">
                  <div data-ai-disclosure="SurveyReportAiGeneratedNotice" className="mb-3">
                    <AiGeneratedNotice />
                  </div>
                  {reportHtmlLoading && <div className="rounded-xl border bg-slate-50 p-4 text-sm text-slate-500">리포트 화면을 불러오는 중입니다.</div>}
                  {reportHtmlError && <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">리포트 화면을 불러오지 못했습니다. PDF 리포트 버튼으로 확인해 주세요.</div>}
                  {!reportHtmlLoading && !reportHtmlError && reportHtml && (
                    <iframe
                      title="보장 상담 리포트"
                      srcDoc={reportHtml}
                      sandbox=""
                      className="h-[70vh] w-full rounded-xl border bg-slate-50"
                    />
                  )}
                </div>
              )}
            </div>

            {session.status === 'IN_PROGRESS' && (
              <div className="flex justify-end">
                <Link to={`/support/surveys/sessions/${session.id}/fill`} className="rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90">
                  작성 이어가기
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function buildStats(sessions, categoryNameByCode = {}) {
  const total = sessions.length
  const completed = sessions.filter(item => item.status === 'SUBMITTED' || item.status === 'ANALYZED').length
  const guided = sessions.filter(item => item.entryMode === 'GUIDED').length
  const remote = sessions.filter(item => item.entryMode === 'REMOTE').length

  const byCategoryMap = new Map()
  const byStatusMap = new Map()
  const recentMap = new Map()
  sessions.forEach(item => {
    const categoryCode = item.categoryCode || 'COMMON'
    const categoryLabel = categoryNameByCode[categoryCode] || categoryCode
    byCategoryMap.set(categoryLabel, (byCategoryMap.get(categoryLabel) || 0) + 1)
    byStatusMap.set(item.status || 'UNKNOWN', (byStatusMap.get(item.status || 'UNKNOWN') || 0) + 1)
    const dateKey = String(item.startedAt || item.submittedAt || '').slice(0, 10) || '미상'
    recentMap.set(dateKey, (recentMap.get(dateKey) || 0) + 1)
  })

  return {
    total,
    completed,
    completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
    guided,
    remote,
    byCategory: Array.from(byCategoryMap.entries()).map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count),
    byStatus: Array.from(byStatusMap.entries()).map(([label, count]) => ({ label, count })),
    recent: Array.from(recentMap.entries()).map(([label, count]) => ({ label, count })).sort((a, b) => a.label.localeCompare(b.label)).slice(-7),
  }
}

function StatBar({ label, count, max }) {
  const width = max > 0 ? Math.max(8, Math.round((count / max) * 100)) : 0
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span>{label}</span>
        <span className="text-slate-500">{count}건</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100">
        <div className="h-2 rounded-full bg-brand-sky" style={{ width: `${width}%` }} />
      </div>
    </div>
  )
}


export default function SurveyManagementPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedSessionId, setSelectedSessionId] = useState(null)
  const [sessionDetailFocusReport, setSessionDetailFocusReport] = useState(false)
  const [openingSessionReportPdf, setOpeningSessionReportPdf] = useState(false)
  const [answerViewMode, setAnswerViewMode] = useState('answer')
  const [historyKeyword, setHistoryKeyword] = useState('')
  const [historyCategory, setHistoryCategory] = useState('ALL')
  const [historyStatus, setHistoryStatus] = useState('ALL')
  const [statsSubtab, setStatsSubtab] = useState('summary')
  const [responseGridCategory, setResponseGridCategory] = useState('ALL')
  const [responseGridPage, setResponseGridPage] = useState(0)
  const [showCustomColumns, setShowCustomColumns] = useState(true)
  const [crmSurveyPrefill, setCrmSurveyPrefill] = useState(null)
  const crmSurveyUrlAppliedRef = useRef(false)

  const activeTab = searchParams.get('tab') || 'list'

  const catalogQuery = useQuery({
    queryKey: ['counsel-surveys', 'catalog'],
    queryFn: async () => unwrap(await api.get('/counsel-surveys/catalog', { __silent: true })),
  })
  const myTemplatesQuery = useQuery({
    queryKey: ['counsel-surveys', 'my-templates', 'list'],
    queryFn: async () => unwrap(await api.get('/counsel-surveys/my-templates', { __silent: true })),
    enabled: activeTab === 'list',
  })
  const sessionsQuery = useQuery({
    queryKey: ['counsel-surveys', 'sessions', 'hub'],
    queryFn: async () => unwrap(await api.get('/counsel-surveys/sessions', { params: { page: 0, size: 200 }, __silent: true })),
  })
  const statsQuery = useQuery({
    queryKey: ['counsel-surveys', 'stats'],
    queryFn: async () => unwrap(await api.get('/counsel-surveys/stats', { __silent: true })),
  })
  const responseGridQuery = useQuery({
    queryKey: ['counsel-surveys', 'response-grid', responseGridCategory, responseGridPage, showCustomColumns],
    enabled: activeTab === 'stats' && statsSubtab === 'data',
    queryFn: async () => unwrap(await api.get('/counsel-surveys/response-grid', {
      params: {
        page: responseGridPage,
        size: 30,
        ...(responseGridCategory !== 'ALL' ? { categoryCode: responseGridCategory } : {}),
      },
      __silent: true,
    })),
  })
  const sessionDetailQuery = useQuery({
    queryKey: ['counsel-surveys', 'session-detail', selectedSessionId],
    enabled: !!selectedSessionId,
    queryFn: async () => unwrap(await api.get(`/counsel-surveys/sessions/${selectedSessionId}`, { __silent: true })),
  })
  const sessionReportQuery = useQuery({
    queryKey: ['counsel-surveys', 'session-report', selectedSessionId],
    enabled: !!selectedSessionId,
    retry: false,
    queryFn: async () => {
      try {
        return unwrap(await api.get(`/counsel-surveys/reports/${selectedSessionId}`, { __silent: true }))
      } catch (e) {
        if (e?.response?.status === 404) return null
        throw e
      }
    },
  })
  const sessionReportHtmlQuery = useQuery({
    queryKey: ['counsel-surveys', 'session-report-html', selectedSessionId],
    enabled: !!selectedSessionId && !!sessionReportQuery.data,
    retry: false,
    queryFn: async () => {
      const res = await api.get(`/counsel-surveys/reports/${selectedSessionId}/html`, {
        responseType: 'text',
        __silent: true,
      })
      return typeof res.data === 'string' ? res.data : ''
    },
  })

  const catalog = useMemo(
    () => (Array.isArray(catalogQuery.data) ? catalogQuery.data : []),
    [catalogQuery.data],
  )
  const myTemplates = useMemo(
    () => (Array.isArray(myTemplatesQuery.data) ? myTemplatesQuery.data : []),
    [myTemplatesQuery.data],
  )
  const catalogLinkedMyTemplateIds = useMemo(
    () => new Set(catalog.map((c) => c?.myTemplate?.id).filter((x) => x != null)),
    [catalog],
  )
  const customMyTemplates = useMemo(
    () => myTemplates.filter((t) => t?.id != null && !catalogLinkedMyTemplateIds.has(t.id)),
    [myTemplates, catalogLinkedMyTemplateIds],
  )
  const sessions = useMemo(
    () => (Array.isArray(sessionsQuery.data?.content) ? sessionsQuery.data.content : []),
    [sessionsQuery.data],
  )
  const sessionDetail = sessionDetailQuery.data || null
  const sessionReport = sessionReportQuery.data || null
  const categoryNameByCode = useMemo(() => Object.fromEntries(
    catalog
      .map(item => [item?.category?.code, item?.category?.name])
      .filter(([code, name]) => !!code && !!name),
  ), [catalog])

  const historyCategories = useMemo(() => ['ALL', ...new Set(catalog.map(item => item?.category?.code).filter(Boolean))], [catalog])
  const historyStatusOptions = useMemo(() => {
    const preferred = ['ALL', 'IN_PROGRESS', 'SUBMITTED', 'ANALYZED']
    const discovered = Array.from(new Set(sessions.map(item => item?.status).filter(Boolean)))
    return preferred.filter(status => status === 'ALL' || discovered.includes(status))
  }, [sessions])
  const filteredSessions = useMemo(() => {
    const keyword = historyKeyword.trim().toLowerCase()
    return sessions.filter(item => {
      if (historyCategory !== 'ALL' && item.categoryCode !== historyCategory) return false
      if (historyStatus !== 'ALL' && item.status !== historyStatus) return false
      if (!keyword) return true
      const targets = [item.customerName, item.templateName, item.categoryCode, categoryNameByCode[item.categoryCode], item.status, getStatusLabel(item.status)].filter(Boolean).join(' ').toLowerCase()
      return targets.includes(keyword)
    })
  }, [sessions, historyCategory, historyStatus, historyKeyword, categoryNameByCode])
  const stats = useMemo(() => {
    if (statsQuery.data && typeof statsQuery.data === 'object') return statsQuery.data
    return buildStats(sessions, categoryNameByCode)
  }, [statsQuery.data, sessions, categoryNameByCode])
  const responseGrid = responseGridQuery.data || {}
  const responseGridFixedColumns = Array.isArray(responseGrid.fixedColumns) ? responseGrid.fixedColumns : []
  const responseGridBaseColumns = Array.isArray(responseGrid.baseQuestionColumns) ? responseGrid.baseQuestionColumns : []
  const responseGridCustomColumns = Array.isArray(responseGrid.customQuestionColumns) ? responseGrid.customQuestionColumns : []
  const responseGridRows = Array.isArray(responseGrid.rows) ? responseGrid.rows : []

  useEffect(() => {
    setResponseGridPage(0)
  }, [responseGridCategory])

  useEffect(() => {
    if (crmSurveyUrlAppliedRef.current) return
    const raw = searchParams.get('customerRefId')
    if (!raw) return
    const id = Number(String(raw).trim())
    if (!Number.isFinite(id) || id <= 0) return
    crmSurveyUrlAppliedRef.current = true
    ;(async () => {
      try {
        const res = await api.get(`/customer-management/customers/${id}/summary`, { __silent: true })
        const data = unwrap(res)
        if (data) {
          setCrmSurveyPrefill({
            id,
            customerName: data.customerName != null ? String(data.customerName) : '',
            customerPhone: data.customerPhone != null ? String(data.customerPhone).replace(/\D/g, '') : '',
            birthDate: data.birthDate != null ? String(data.birthDate) : '',
          })
        }
        const next = new URLSearchParams(searchParams)
        next.delete('customerRefId')
        setSearchParams(next, { replace: true })
        try { window?.toast?.show?.('고객 정보를 불러왔습니다. 설문을 시작하면 해당 고객으로 연결됩니다.', 'success') } catch {}
      } catch (_) {
        crmSurveyUrlAppliedRef.current = false
      }
    })()
  }, [searchParams, setSearchParams])

  useEffect(() => {
    const raw = searchParams.get('openSession')
    if (!raw) return
    const sid = Number(String(raw).trim())
    if (!Number.isFinite(sid) || sid <= 0) return

    const focusReport = searchParams.get('focusReport') === '1'

    let cancelled = false
    ;(async () => {
      await qc.invalidateQueries({ queryKey: ['counsel-surveys', 'sessions', 'hub'] })
      if (cancelled) return
      setSessionDetailFocusReport(focusReport)
      setSelectedSessionId(sid)
      setAnswerViewMode('question')
      const next = new URLSearchParams(searchParams)
      next.delete('openSession')
      next.delete('focusReport')
      setSearchParams(next, { replace: true })
    })()

    return () => {
      cancelled = true
    }
  }, [searchParams, setSearchParams, qc])

  const refreshAll = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['counsel-surveys', 'catalog'] }),
      qc.invalidateQueries({ queryKey: ['counsel-surveys', 'my-templates', 'list'] }),
      qc.invalidateQueries({ queryKey: ['counsel-surveys', 'sessions', 'hub'] }),
      qc.invalidateQueries({ queryKey: ['counsel-surveys', 'stats'] }),
      qc.invalidateQueries({ queryKey: ['counsel-surveys', 'session-detail'] }),
      qc.invalidateQueries({ queryKey: ['counsel-surveys', 'session-report'] }),
    ])
  }

  const buildSelectedSurveyCustomer = () => {
    const source = crmSurveyPrefill || {}
    const id = Number(source.id)
    const customerName = String(source.customerName || '').trim()
    const customerPhone = String(source.customerPhone || '').replace(/\D/g, '')
    const birthDate = source.birthDate != null ? String(source.birthDate).slice(0, 10) : ''
    if ((!Number.isFinite(id) || id <= 0) && !customerName && !customerPhone && !birthDate) return null
    return {
      id: Number.isFinite(id) && id > 0 ? id : null,
      customerName,
      customerPhone,
      birthDate,
    }
  }

  const applySelectedSurveyCustomer = (body) => {
    const selected = buildSelectedSurveyCustomer()
    if (!selected) return false
    if (selected.id) body.customerId = selected.id
    if (selected.customerName) body.customerName = selected.customerName
    if (selected.customerPhone) body.customerPhone = selected.customerPhone
    if (selected.birthDate) body.customerBirthDate = selected.birthDate
    return true
  }

  const startSurveyMutation = useMutation({
    mutationFn: async (payload) => {
      const body = {
        channel: 'WEB',
        entryMode: 'GUIDED',
      }
      if (payload?.myTemplateId) {
        body.myTemplateId = payload.myTemplateId
      } else {
        body.templateId = payload?.template?.id
        body.categoryCode = payload?.category?.code
      }
      applySelectedSurveyCustomer(body)
      return unwrap(await api.post('/counsel-surveys/sessions', body))
    },
    onSuccess: async (data) => {
      try { window?.toast?.show?.('설문을 시작합니다.', 'success') } catch {}
      setCrmSurveyPrefill(null)
      await refreshAll()
      navigate(`/support/surveys/sessions/${data.id}/fill`)
    },
    onError: (e) => {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '설문 시작 실패', 'error') } catch {}
    },
  })

  const shareLinkMutation = useMutation({
    mutationFn: async (payload) => {
      const customName = payload?.myTemplateId ? (payload.name || payload.label || '') : ''
      if (buildSelectedSurveyCustomer()) {
        const sessionBody = {
          channel: 'WEB',
          entryMode: 'REMOTE',
        }
        if (payload?.myTemplateId) {
          sessionBody.myTemplateId = payload.myTemplateId
        } else {
          sessionBody.templateId = payload?.template?.id
          sessionBody.categoryCode = payload?.category?.code
        }
        applySelectedSurveyCustomer(sessionBody)
        const session = unwrap(await api.post('/counsel-surveys/sessions', sessionBody))
        if (!session?.id) throw new Error('설문 링크 발급에 실패했습니다.')
        const token = unwrap(await api.post(`/counsel-surveys/sessions/${session.id}/tokens`, { expireHours: 24 * 30 }))
        return { token, item: payload?.myTemplateId ? null : payload, customName, customerLinked: true }
      }
      const reqBody = payload?.myTemplateId
        ? { myTemplateId: payload.myTemplateId, expireHours: 24 * 30 }
        : {
          templateId: payload?.template?.id,
          categoryCode: payload?.category?.code,
          expireHours: 24 * 30,
        }
      const token = unwrap(await api.post('/counsel-surveys/share-links', reqBody))
      return { token, item: payload?.myTemplateId ? null : payload, customName }
    },
    onSuccess: async ({ token, item, customName, customerLinked }) => {
      const url = buildPublicCounselSurveyUrl(token)
      if (url) {
        const tplName = customName || item?.myTemplate?.name || item?.template?.name || ''
        const title = tplName ? `설문 안내 · ${tplName}` : '설계EZ 설문 안내'
        const desc = '아래 링크에서 설문에 참여해 주세요.'
        const usedKakao = tryShareFeedToKakao({ title, description: desc, url })
        if (!usedKakao) {
          try { await navigator.clipboard.writeText(url) } catch {}
        }
        try {
          window?.toast?.show?.(
            usedKakao ? '카카오톡 공유를 실행했습니다. 링크는 클립보드에도 복사했습니다.' : '링크를 복사했습니다.',
            'success'
          )
        } catch {}
        if (usedKakao) {
          try { await navigator.clipboard.writeText(url) } catch {}
        }
      } else {
        try { window?.toast?.show?.('링크를 발급했습니다.', 'success') } catch {}
      }
      if (customerLinked) setCrmSurveyPrefill(null)
      await refreshAll()
      setSearchParams({ tab: 'history' })
    },
    onError: (e) => {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '링크 공유 실패', 'error') } catch {}
    },
  })

  const shareHistoryLinkMutation = useMutation({
    mutationFn: async (sessionId) => unwrap(await api.post(`/counsel-surveys/sessions/${sessionId}/tokens`, { expireHours: 24 * 7 })),
    onSuccess: async (token) => {
      const url = buildPublicCounselSurveyUrl(token)
      if (url) {
        const usedKakao = tryShareFeedToKakao({
          title: '설문 이어쓰기 안내',
          description: '아래 링크에서 설문 작성을 이어가 주세요.',
          url,
        })
        if (!usedKakao) {
          try { await navigator.clipboard.writeText(url) } catch {}
        }
        try {
          window?.toast?.show?.(
            usedKakao ? '카카오톡 공유를 실행했습니다. 링크는 클립보드에도 복사했습니다.' : '고객 이어쓰기 링크를 복사했습니다.',
            'success'
          )
        } catch {}
        if (usedKakao) {
          try { await navigator.clipboard.writeText(url) } catch {}
        }
      } else {
        try { window?.toast?.show?.('고객 이어쓰기 링크를 발급했습니다.', 'success') } catch {}
      }
    },
    onError: (e) => {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '고객 링크 공유 실패', 'error') } catch {}
    },
  })

  const updateSessionMutation = useMutation({
    mutationFn: async ({ sessionId, payload }) => unwrap(await api.patch(`/counsel-surveys/sessions/${sessionId}`, payload)),
    onSuccess: async (_, variables) => {
      try { window?.toast?.show?.('설문 이력을 수정했습니다.', 'success') } catch {}
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'sessions', 'hub'] }),
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'session-detail', variables.sessionId] }),
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'session-report', variables.sessionId] }),
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'stats'] }),
      ])
    },
    onError: (e) => {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '설문 이력 수정 실패', 'error') } catch {}
    },
  })

  const deleteSessionMutation = useMutation({
    mutationFn: async (sessionId) => unwrap(await api.delete(`/counsel-surveys/sessions/${sessionId}`)),
    onSuccess: async (_, sessionId) => {
      try { window?.toast?.show?.('설문 이력을 삭제했습니다.', 'success') } catch {}
      if (selectedSessionId === sessionId) {
        setSelectedSessionId(null)
        setSessionDetailFocusReport(false)
      }
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'sessions', 'hub'] }),
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'stats'] }),
      ])
    },
    onError: (e) => {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '설문 이력 삭제 실패', 'error') } catch {}
    },
  })

  const registerCustomerFromSessionMutation = useMutation({
    mutationFn: async (sessionId) => unwrap(await api.post(`/counsel-surveys/sessions/${sessionId}/customer-management`)),
    onSuccess: async () => {
      try { window?.toast?.show?.('고객관리에 등록했습니다.', 'success') } catch {}
      await qc.invalidateQueries({ queryKey: ['counsel-surveys', 'sessions', 'hub'] })
    },
    onError: (e) => {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '고객 등록에 실패했습니다.', 'error') } catch {}
    },
  })

  const generateReportMutation = useMutation({
    mutationFn: async () => unwrapAsyncResult(await api.post(`/counsel-surveys/reports/${selectedSessionId}/generate`)),
    onSuccess: async () => {
      try { window?.toast?.show?.('분석 리포트를 갱신했습니다.', 'success') } catch {}
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'session-detail', selectedSessionId] }),
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'session-report', selectedSessionId] }),
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'session-report-html', selectedSessionId] }),
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'sessions', 'hub'] }),
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'stats'] }),
      ])
    },
    onError: (e) => {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '리포트 생성 실패', 'error') } catch {}
    },
  })

  const openSessionReportPdf = async (sessionId) => {
    if (!sessionId) return
    setOpeningSessionReportPdf(true)
    try {
      await openCounselSurveyReportPdf(api, sessionId)
    } catch (e) {
      try { window?.toast?.show?.(e?.message || '상담 리포트 PDF를 열지 못했습니다.', 'error') } catch {}
    } finally {
      setOpeningSessionReportPdf(false)
    }
  }

  const exportResponseGridCsvMutation = useMutation({
    mutationFn: async () => {
      const pageSize = 100
      const first = unwrap(await api.get('/counsel-surveys/response-grid', {
        params: {
          page: 0,
          size: pageSize,
          ...(responseGridCategory !== 'ALL' ? { categoryCode: responseGridCategory } : {}),
        },
        __silent: true,
      }))
      const totalPages = Math.max(Number(first?.totalPages || 0), 1)
      let rows = Array.isArray(first?.rows) ? [...first.rows] : []
      for (let page = 1; page < totalPages; page++) {
        const next = unwrap(await api.get('/counsel-surveys/response-grid', {
          params: {
            page,
            size: pageSize,
            ...(responseGridCategory !== 'ALL' ? { categoryCode: responseGridCategory } : {}),
          },
          __silent: true,
        }))
        rows = rows.concat(Array.isArray(next?.rows) ? next.rows : [])
      }
      return {
        fixedColumns: Array.isArray(first?.fixedColumns) ? first.fixedColumns : [],
        baseColumns: Array.isArray(first?.baseQuestionColumns) ? first.baseQuestionColumns : [],
        customColumns: Array.isArray(first?.customQuestionColumns) ? first.customQuestionColumns : [],
        rows,
      }
    },
    onSuccess: (data) => {
      const fixedColumns = data.fixedColumns || []
      const baseColumns = data.baseColumns || []
      const customColumns = showCustomColumns ? (data.customColumns || []) : []
      const allColumns = [...fixedColumns, ...baseColumns, ...customColumns]

      const csvRows = [
        allColumns.map((c) => c.label || c.key),
        ...data.rows.map((row) => allColumns.map((column) => {
          if (fixedColumns.some((f) => f.key === column.key)) {
            const raw = row?.[column.key]
            if (column.key === 'status') return getStatusLabel(raw)
            if (column.key === 'occurredAt') return formatDateTimeCompact(raw)
            return raw ?? ''
          }
          return row?.answers?.[column.key] ?? ''
        })),
      ]
      const now = new Date()
      const date = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
      const categorySuffix = responseGridCategory === 'ALL' ? 'ALL' : responseGridCategory
      downloadCsv(`survey-response-grid-${categorySuffix}-${date}.csv`, csvRows)
      try { window?.toast?.show?.('응답데이터 CSV를 다운로드했습니다.', 'success') } catch {}
    },
    onError: (e) => {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || 'CSV 다운로드 실패', 'error') } catch {}
    },
  })

  if (catalogQuery.isLoading || sessionsQuery.isLoading || (activeTab === 'list' && myTemplatesQuery.isLoading)) return <Loading />
  if (catalogQuery.isError) return <InlineError message={catalogQuery.error?.message || '설문 목록을 불러오지 못했습니다.'} />
  if (sessionsQuery.isError) return <InlineError message={sessionsQuery.error?.message || '설문 이력을 불러오지 못했습니다.'} />
  if (statsQuery.isError) return <InlineError message={statsQuery.error?.message || '설문 통계를 불러오지 못했습니다.'} />

  const maxCategoryCount = Math.max(...stats.byCategory.map(item => item.count), 0)
  const maxRecentCount = Math.max(...stats.recent.map(item => item.count), 0)

  return (
    <PageFrame>
      <PageHeader
        title="설문관리"
        description="고객지원"
        titleAccessory={<><AiFeatureBadge /><MenuGuideButton slug="surveys" title="설문관리 가이드" /></>}
        actions={(
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={refreshAll} className="rounded border px-3 py-2 text-sm hover:bg-slate-50">새로고침</button>
          <button
            type="button"
            onClick={() => navigate('/support/surveys/create')}
            className="rounded-lg bg-teal-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-800"
          >
            설문 생성
          </button>
        </div>
        )}
      />

      <div className="mx-auto w-full max-w-6xl">
      <div className="flex flex-wrap justify-end gap-2">
        {['list', 'history', 'stats'].map(tab => (
          <button
            key={tab}
            type="button"
            onClick={() => setSearchParams({ tab })}
            className={`rounded-full border px-4 py-2 text-sm font-semibold ${activeTab === tab ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
          >
            {getTabLabel(tab)}
          </button>
        ))}
      </div>

      {activeTab === 'list' && (
        <section className="mt-5 max-w-md">
          <label className="block text-sm font-semibold text-slate-700">설문 대상 고객</label>
          <ManagedCustomerAutocomplete
            value={crmSurveyPrefill?.customerName || ''}
            onValueChange={(next) => {
              const name = String(next || '')
              setCrmSurveyPrefill(name.trim()
                ? { customerName: name, customerPhone: '', birthDate: '' }
                : null)
            }}
            selectedCustomerId={crmSurveyPrefill?.id || ''}
            onSelectCustomer={(customer) => setCrmSurveyPrefill({
              id: Number(customer.id),
              customerName: customer.customerName || '',
              customerPhone: customer.customerPhone != null ? String(customer.customerPhone).replace(/\D/g, '') : '',
              birthDate: customer.birthDate != null ? String(customer.birthDate).slice(0, 10) : '',
            })}
            onClearCustomer={() => setCrmSurveyPrefill(null)}
            inputClassName="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
            placeholder="고객명 입력"
          />
        </section>
      )}

      {activeTab === 'list' && (
        <div className="mt-6 space-y-10">
          <section>
            <div className="mb-1 border-l-4 border-brand-navy pl-3">
              <h2 className="text-base font-semibold text-slate-900">카테고리별 기본 설문</h2>
            </div>
            <div className="mt-4 space-y-3">
              {catalog.map((item, catIdx) => (
                <article
                  key={item?.category?.code != null ? String(item.category.code) : `cat-${catIdx}`}
                  className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-lg font-semibold text-slate-900">{item?.category?.name || '카테고리'}</h3>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${item?.ready ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {item?.ready ? '사용 가능' : '준비 중'}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{item?.category?.description || '상담 전 확인이 필요한 영역입니다.'}</p>
                    {/* {item?.template?.description ? (
                      <p className="mt-2 line-clamp-2 text-xs text-slate-500">{item.template.description}</p>
                    ) : null} */}
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={!item?.ready || startSurveyMutation.isPending}
                      onClick={() => startSurveyMutation.mutate(item)}
                      className="rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                    >
                      설문시작
                    </button>
                    <button
                      type="button"
                      disabled={!item?.ready || shareLinkMutation.isPending}
                      onClick={() => shareLinkMutation.mutate(item)}
                      className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                    >
                      링크공유
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section>
            <div className="mb-1 border-l-4 border-teal-600 pl-3">
              <h2 className="text-base font-semibold text-slate-900">내 맞춤 설문</h2>
            </div>
            {customMyTemplates.length === 0 ? (
              <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 px-4 py-8 text-center text-sm text-slate-500">
                아직 맞춤 설문이 없습니다. 상단 「설문 생성」으로 설문을 직접 만들어보세요.
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                {customMyTemplates.map((t) => (
                  <article
                    key={t.id}
                    className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0 flex-1">
                      <h3 className="text-lg font-semibold text-slate-900">{t.name || `맞춤 설문 #${t.id}`}</h3>
                      <p className="mt-1 text-xs text-slate-500">마지막 수정 {formatDateTime(t.updatedAt)}</p>
                    </div>
                    <div className="flex shrink-0 flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={startSurveyMutation.isPending}
                        onClick={() => startSurveyMutation.mutate({ myTemplateId: t.id })}
                        className="rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                      >
                        설문시작
                      </button>
                      <button
                        type="button"
                        disabled={shareLinkMutation.isPending}
                        onClick={() => shareLinkMutation.mutate({ myTemplateId: t.id, name: t.name })}
                        className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                      >
                        링크공유
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate(`/support/surveys/my/${t.id}/edit`)}
                        className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      >
                        편집
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="mt-6 space-y-4">
          <section className="rounded-2xl border bg-white p-5">
            <div className="grid gap-3 md:grid-cols-[1.6fr_0.8fr]">
              <input
                className="rounded-lg border px-3 py-2 text-sm"
                placeholder="고객명, 설문명, 상태로 검색"
                value={historyKeyword}
                onChange={(e) => setHistoryKeyword(e.target.value)}
              />
              <select className="rounded-lg border px-3 py-2 text-sm" value={historyCategory} onChange={(e) => setHistoryCategory(e.target.value)}>
                {historyCategories.map(code => <option key={code} value={code}>{code === 'ALL' ? '전체 카테고리' : (categoryNameByCode[code] || code)}</option>)}
              </select>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {historyStatusOptions.map(status => (
                <button
                  key={status}
                  type="button"
                  onClick={() => setHistoryStatus(status)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold ${
                    historyStatus === status
                      ? 'border border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10'
                      : 'border bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {status === 'ALL' ? '전체 상태' : getStatusLabel(status)}
                </button>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border bg-white p-5">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-semibold">진행 이력</h2>
              <span className="text-sm text-slate-500">{filteredSessions.length}건</span>
            </div>
            <div className="mt-4 space-y-3">
              {filteredSessions.length === 0 && <div className="rounded-xl border border-dashed p-6 text-sm text-slate-400">조건에 맞는 설문 이력이 없습니다.</div>}
              {filteredSessions.map(item => (
                <div key={item.id} className="rounded-xl border p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-medium">{item.customerName || '고객명 미입력'}</div>
                      <div className="mt-1 text-sm text-slate-600">{item.templateName || categoryNameByCode[item.categoryCode] || item.categoryCode || '설문'}</div>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                        <span>{item.entryMode === 'REMOTE' ? '링크작성' : '직접작성'}</span>
                        <span className={`rounded-full px-2 py-1 font-semibold ${
                          item.status === 'ANALYZED'
                            ? 'bg-emerald-50 text-emerald-700'
                            : item.status === 'SUBMITTED'
                              ? 'bg-sky-50 text-sky-700'
                              : 'bg-amber-50 text-amber-700'
                        }`}>
                          {getStatusLabel(item.status)}
                        </span>
                        <span>진행률 {item.progressPercent ?? 0}%</span>
                        <span>{formatDateTime(item.startedAt)}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {item.entryMode === 'REMOTE' && item.status === 'IN_PROGRESS' && (
                        <button
                          type="button"
                          onClick={() => shareHistoryLinkMutation.mutate(item.id)}
                          disabled={shareHistoryLinkMutation.isPending}
                          className="rounded-full border border-brand-sky bg-sky-50 px-3 py-1.5 text-xs font-semibold text-brand-navy hover:bg-sky-100 disabled:opacity-60"
                        >
                          이어쓰기 링크
                        </button>
                      )}
                      {!item.customerId ? (
                        <button
                          type="button"
                          onClick={() => {
                            if (!window.confirm('설문 응답에 있는 연락처로 고객관리에 등록할까요? (이미 동일 연락처 고객이 있으면 해당 고객에 연결됩니다.)')) return
                            registerCustomerFromSessionMutation.mutate(item.id)
                          }}
                          disabled={registerCustomerFromSessionMutation.isPending}
                          className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-900 hover:bg-emerald-100 disabled:opacity-60"
                        >
                          고객등록
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => {
                          if (!window.confirm('이 설문 이력을 삭제할까요? 삭제 후 복구할 수 없습니다.')) return
                          deleteSessionMutation.mutate(item.id)
                        }}
                        disabled={deleteSessionMutation.isPending}
                        className="rounded-lg border border-red-200 px-4 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-60"
                      >
                        삭제
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSessionDetailFocusReport(false)
                          setAnswerViewMode('answer')
                          setSelectedSessionId(item.id)
                        }}
                        className="rounded-lg border px-4 py-2 text-sm hover:bg-slate-50"
                      >
                        상세 보기
                      </button>
                      {(item.status === 'SUBMITTED' || item.status === 'ANALYZED') && (
                        <button
                          type="button"
                          onClick={() => openSessionReportPdf(item.id)}
                          disabled={openingSessionReportPdf}
                          className="rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                        >
                          PDF 리포트
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {activeTab === 'stats' && (
        <div className="mt-6 space-y-4">
          <div className="flex flex-wrap gap-2">
            {[
              ['summary', '요약통계'],
              ['data', '응답데이터'],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setStatsSubtab(key)}
                className={`rounded-full border px-4 py-2 text-sm font-semibold ${statsSubtab === key ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                {label}
              </button>
            ))}
          </div>

          {statsSubtab === 'summary' && (
            <>
              <div className="grid gap-4 md:grid-cols-4">
                {[
                  ['전체 설문', `${stats.total}건`],
                  ['완료율', `${stats.completionRate}%`],
                  ['직접작성', `${stats.guided}건`],
                  ['링크작성', `${stats.remote}건`],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-2xl border bg-white p-5">
                    <div className="text-sm text-slate-500">{label}</div>
                    <div className="mt-2 text-2xl font-bold">{value}</div>
                  </div>
                ))}
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <section className="rounded-2xl border bg-white p-5">
                  <h2 className="font-semibold">카테고리별 진행 건수</h2>
                  <div className="mt-4 space-y-3">
                    {stats.byCategory.map(item => <StatBar key={item.label} label={item.label} count={item.count} max={maxCategoryCount} />)}
                    {stats.byCategory.length === 0 && <div className="text-sm text-slate-400">표시할 데이터가 없습니다.</div>}
                  </div>
                </section>

                <section className="rounded-2xl border bg-white p-5">
                  <h2 className="font-semibold">최근 7일 추이</h2>
                  <div className="mt-4 space-y-3">
                    {stats.recent.map(item => <StatBar key={item.label} label={item.label} count={item.count} max={maxRecentCount} />)}
                    {stats.recent.length === 0 && <div className="text-sm text-slate-400">표시할 데이터가 없습니다.</div>}
                  </div>
                </section>
              </div>
            </>
          )}

          {statsSubtab === 'data' && (
            <section className="rounded-2xl border bg-white p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="font-semibold">응답데이터</h2>
                  <div className="mt-1 text-sm text-slate-500">기본 문항은 고정 컬럼으로, 커스텀 문항은 뒤쪽 확장 컬럼으로 조회합니다.</div>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    checked={showCustomColumns}
                    onChange={(e) => setShowCustomColumns(e.target.checked)}
                  />
                  커스텀 문항 포함
                </label>
              </div>

              <div className="mt-4 flex flex-wrap gap-3">
                <select
                  className="rounded-lg border px-3 py-2 text-sm"
                  value={responseGridCategory}
                  onChange={(e) => setResponseGridCategory(e.target.value)}
                >
                  {historyCategories.map(code => <option key={code} value={code}>{code === 'ALL' ? '전체 카테고리' : (categoryNameByCode[code] || code)}</option>)}
                </select>
                <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-500">
                  총 {responseGrid.totalElements ?? 0}건
                </div>
                <button
                  type="button"
                  onClick={() => exportResponseGridCsvMutation.mutate()}
                  disabled={exportResponseGridCsvMutation.isPending}
                  className="rounded-lg border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-60"
                >
                  {exportResponseGridCsvMutation.isPending ? '다운로드 준비 중...' : 'CSV 다운로드'}
                </button>
              </div>

              {responseGridQuery.isLoading && <div className="mt-4 text-sm text-slate-500">응답데이터를 불러오는 중입니다.</div>}
              {responseGridQuery.isError && <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">응답데이터를 불러오지 못했습니다.</div>}

              {!responseGridQuery.isLoading && !responseGridQuery.isError && (
                <>
                  <div className="mt-4 overflow-x-auto rounded-xl border">
                    <table className="min-w-full border-collapse text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          {responseGridFixedColumns.map((column) => (
                            <th key={column.key} className="whitespace-nowrap border-b px-3 py-2 text-left font-semibold text-slate-700">
                              {column.label}
                            </th>
                          ))}
                          {responseGridBaseColumns.map((column) => (
                            <th key={column.key} className="whitespace-nowrap border-b px-3 py-2 text-left font-semibold text-slate-700">
                              {column.label}
                            </th>
                          ))}
                          {showCustomColumns && responseGridCustomColumns.map((column) => (
                            <th key={column.key} className="whitespace-nowrap border-b px-3 py-2 text-left font-semibold text-slate-700">
                              {column.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {responseGridRows.map((row) => (
                          <tr key={row.sessionId} className="odd:bg-white even:bg-slate-50/40">
                            {responseGridFixedColumns.map((column) => (
                              <td key={`${row.sessionId}-${column.key}`} className="whitespace-nowrap border-b px-3 py-2 text-slate-700">
                                {column.key === 'status'
                                  ? getStatusLabel(row[column.key])
                                  : column.key === 'occurredAt'
                                    ? formatDateTimeCompact(row[column.key])
                                    : (row[column.key] || '-')}
                              </td>
                            ))}
                            {responseGridBaseColumns.map((column) => (
                              <td key={`${row.sessionId}-${column.key}`} className="min-w-[180px] border-b px-3 py-2 text-slate-700">
                                {row.answers?.[column.key] || '-'}
                              </td>
                            ))}
                            {showCustomColumns && responseGridCustomColumns.map((column) => (
                              <td key={`${row.sessionId}-${column.key}`} className="min-w-[180px] border-b px-3 py-2 text-slate-700">
                                {row.answers?.[column.key] || '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {responseGridRows.length === 0 && (
                    <div className="mt-4 rounded-xl border border-dashed p-6 text-center text-sm text-slate-400">
                      조건에 맞는 응답데이터가 없습니다.
                    </div>
                  )}

                  <div className="mt-4 flex items-center justify-between gap-3">
                    <div className="text-sm text-slate-500">
                      페이지 {(responseGrid.page ?? 0) + 1} / {Math.max(responseGrid.totalPages ?? 0, 1)}
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setResponseGridPage((prev) => Math.max(0, prev - 1))}
                        disabled={(responseGrid.page ?? 0) <= 0}
                        className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                      >
                        이전
                      </button>
                      <button
                        type="button"
                        onClick={() => setResponseGridPage((prev) => prev + 1)}
                        disabled={(responseGrid.page ?? 0) >= Math.max((responseGrid.totalPages ?? 1) - 1, 0)}
                        className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                      >
                        다음
                      </button>
                    </div>
                  </div>
                </>
              )}
            </section>
          )}
        </div>
      )}

      {selectedSessionId && (
        <SessionDetailModal
          session={sessionDetail}
          report={sessionReport}
          loading={sessionDetailQuery.isLoading || sessionReportQuery.isLoading}
          error={sessionDetailQuery.isError}
          onClose={() => {
            setSelectedSessionId(null)
            setSessionDetailFocusReport(false)
          }}
          onRegenerate={() => generateReportMutation.mutate()}
          regenerating={generateReportMutation.isPending}
          onOpenPdf={() => openSessionReportPdf(selectedSessionId)}
          openingPdf={openingSessionReportPdf}
          reportHtml={sessionReportHtmlQuery.data || ''}
          reportHtmlLoading={sessionReportHtmlQuery.isLoading}
          reportHtmlError={sessionReportHtmlQuery.isError}
          answerViewMode={answerViewMode}
          onChangeAnswerViewMode={setAnswerViewMode}
          onSaveEdit={(payload) => updateSessionMutation.mutateAsync({ sessionId: selectedSessionId, payload })}
          savingEdit={updateSessionMutation.isPending}
          onDelete={async (sessionId) => {
            await deleteSessionMutation.mutateAsync(sessionId)
            setSelectedSessionId(null)
            setSessionDetailFocusReport(false)
          }}
          deleting={deleteSessionMutation.isPending}
          scrollReportIntoView={sessionDetailFocusReport}
        />
      )}
      </div>
    </PageFrame>
  )
}
